<template>
	<!-- AdServer Promotion Placement - Only renders when ad server returns a valid creative -->
	<div>
		<SingleQTV
			v-if="adServerCreativeType === AdServerCreativeType.SQTV"
			:title="title"
			:catalogueItem="catalogueItem"
			:additionalContexts="additionalContexts"
			:adServerCreative="adServerCreative"
		/>

		<QualityTV
			v-else-if="adServerCreativeType === AdServerCreativeType.QTV"
			:title="title"
			:catalogueItem="catalogueItem"
			:additionalContexts="qtvAdditionalContexts"
			:seasonNumber="seasonNumber"
			:episodeNumber="episodeNumber"
			:offers="offers"
			:adServerCreative="adServerCreative"
		/>

		<ListPromotionRow
			v-else-if="adServerCreativeType === AdServerCreativeType.FREE_TRIAL"
			:offers="offers"
			:title="title"
			:additionalContexts="additionalContexts"
			:episodeNumber="episodeNumber"
			:adServerCreative="adServerCreative"
			@clickOut="$emit('clickOut', $event)"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { usePromotion } from '@/helpers/composables/usePromotion';
import { useAdServer } from '@/features/ad-server/composables/useAdServer';
import { AdServerCreativeType } from '@/features/ad-server/types';

import { type Offer } from '@/@types/graphql-types';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { OfferLocation } from '@/helpers/offer-helper';

const ListPromotionRow = defineAsyncComponent(() => import('@/features/buybox/components/list/ListPromotionRow.vue'));
const QualityTV = defineAsyncComponent(() => import('@/features/buybox/components/list/QualityTV.vue'));
const SingleQTV = defineAsyncComponent(() => import('@/features/buybox/components/list/SingleQTV.vue'));

const props = defineProps<{
	offers: Offer[];
	title: TitleDetail;
	additionalContexts: SnowplowContext[];
	trackingContexts: SnowplowContext[];
	seasonNumber?: number | null;
	episodeNumber?: number | null;
}>();

defineEmits<{
	(
		e: 'clickOut',
		payload: {
			buyboxOffer: TitleOfferFragment | null;
			offerLocation: OfferLocation.FREE_TRIAL;
			row: HTMLElement | null;
		}
	): void;
}>();

const { adServerCreative } = useAdServer(() => props.title.id);

const adServerCreativeType = computed(() => adServerCreative.value?.__typename ?? null);

const { catalogueItem } = usePromotion({
	title: () => props.title,
	offers: () => props.offers,
	hardcodedShortName: computed(() => null),
});

const qtvAdditionalContexts = computed(() => props.additionalContexts.concat(props.trackingContexts));
</script>
